/**
 * SizilPro — useAIWriter
 * Client-side hook for calling the ai-proposal-writer Edge Function.
 * All Claude API calls route through the Edge Function — never direct from browser.
 */

import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CoverLetterAnswers } from "../types";

const FUNCTION_NAME = "ai-proposal-writer";

export interface CoverLetterResult {
  cover_letter_html?: string;
  coverLetter?: string;
  signoff?: string | null;
  suggested_tagline?: string | null;
}

export interface ScopeItemInput {
  name: string;
  description?: string | null;
  ai_generated_description?: string | null;
  price?: number | null;
  quantity?: number | null;
  price_unit?: string | null;
  frequency?: string | null;
  duration_hours?: number | null;
  focus_area?: string | null;
}

export interface ScopeItemResult {
  title?: string;
  description: string;
}

// ─── Shared caller ────────────────────────────────────────────
async function callAIWriter<T>(
  action: string,
  payload: Record<string, unknown>
): Promise<T> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();

  if (sessionError || !sessionData.session) {
    throw new Error("Not authenticated");
  }

  const token = sessionData.session.access_token;

  const { data, error } = await supabase.functions.invoke<{
    success: boolean;
    data: T;
    error?: string;
  }>(FUNCTION_NAME, {
    body: { action, payload },
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: (import.meta.env as Record<string, string>).VITE_SUPABASE_ANON_KEY,
    },
  });

  if (error) throw new Error(error.message || "Edge Function error");
  if (!data?.success) throw new Error(data?.error || "AI call failed");

  return data.data;
}

// ─── Cover letter generation (Sonnet) ─────────────────────────
export function useGenerateCoverLetter() {
  return useMutation({
    mutationFn: (answers: CoverLetterAnswers) =>
      callAIWriter<CoverLetterResult>("generate_cover_letter", { answers }),
  });
}

// ─── Project summary polish (Haiku) ──────────────────────────
export function usePolishProjectSummary() {
  return useMutation({
    mutationFn: ({
      rawSummary,
      proposalId,
    }: {
      rawSummary: string;
      proposalId?: string;
    }) =>
      callAIWriter<{ polished_summary: string }>("polish_project_summary", {
        rawSummary,
        proposalId,
      }),
  });
}

// ─── Template context preview (Haiku) ───────────────────────
export function usePreviewTemplateContext() {
  return useMutation({
    mutationFn: ({
      tone,
      industryContext,
      serviceContext,
    }: {
      tone: string;
      industryContext?: string;
      serviceContext?: string;
    }) =>
      callAIWriter<{ preview: string }>("preview_template_context", {
        tone,
        industryContext,
        serviceContext,
      }),
  });
}

// ─── Static page draft (Haiku) ───────────────────────────────
export function useDraftStaticPage() {
  return useMutation({
    mutationFn: ({
      pageTitle,
      pageKind,
      businessContext,
    }: {
      pageTitle: string;
      pageKind: string;
      businessContext?: string;
    }) =>
      callAIWriter<{ content: string }>("draft_static_page", {
        pageTitle,
        pageKind,
        businessContext,
      }),
  });
}

// ─── Template bundle suggestion (Haiku) ─────────────────────
export interface TemplateBundleServiceInput {
  id: string;
  name: string;
  category?: string | null;
}

export interface TemplateBundlePageInput {
  id: string;
  title: string;
  pageKind?: string | null;
}

export interface TemplateBundleResult {
  suggestedServiceIds: string[];
  suggestedPageIds: string[];
  rationale: string;
}

export function useSuggestTemplateBundle() {
  return useMutation({
    mutationFn: ({
      clientType,
      clientTypeNotes,
      availableServices,
      availablePages,
    }: {
      clientType: string;
      clientTypeNotes?: string;
      availableServices: TemplateBundleServiceInput[];
      availablePages: TemplateBundlePageInput[];
    }) =>
      callAIWriter<TemplateBundleResult>("suggest_template_bundle", {
        clientType,
        clientTypeNotes,
        availableServices,
        availablePages,
      }),
  });
}

// ─── Scope descriptions (Haiku) ──────────────────────────────
export function useGenerateScopeItems() {
  return useMutation({
    mutationFn: ({
      lineItems,
      projectContext,
      brandVoice,
    }: {
      lineItems: ScopeItemInput[];
      projectContext?: string;
      brandVoice?: string;
    }) =>
      callAIWriter<{ line_items: ScopeItemResult[] }>("generate_scope_items", {
        lineItems,
        projectContext,
        brandVoice,
      }),
  });
}